import type { GetProductInfoTempQuery } from '@schema/cart';
import type { ProductInfoI } from './index.type';
import type { Badge } from '@utils/prepareBadges';
import {
  prepareAllBadges,
  prepareBottomBadges,
  prepareNewBadges,
  prepareOldBadges
} from '@utils/prepareBadges';
import { numberTextFormat } from '@utils/declension';
import { getPreorderDateLabel } from '@utils/getPreorderDateLabel';
import { preparePriceFloatObject } from '@utils/floatNumbers';
import { getProductCanBeBought } from '@utils/getProductCanBeBought';

interface PrepareProductRes {
  productInfo: ProductInfoI;
  isProductAvailableForRefund: boolean;
}

export const prepareProductRes = ({
  res,
  nearestAvailableIntervalValue,
  isBarMenu,
  pathname
}: {
  res: NonNullable<GetProductInfoTempQuery>;
  nearestAvailableIntervalValue?: any;
  isBarMenu?: boolean;
  pathname: string;
}): PrepareProductRes => {
  const productRes = res.getProductByIdTemp;

  const multiPricePromo = productRes.multiPrices?.length
    ? productRes.multiPrices?.find(
        item => typeof item.buy_at_once === 'number' && item.buy_at_once > 0
      ) ?? null
    : null;

  const isBuyAtOncePromo =
    multiPricePromo &&
    typeof multiPricePromo?.buy_at_once === 'number' &&
    multiPricePromo?.buy_at_once > 0;

  const isDigital = productRes.digital === 1;

  let preparedBadges: Badge[] = [];
  let preparedNewBadges: Badge[] = [];
  let preparedBottomBadges: Badge[] = [];

  const productAsGift = productRes?.promocodeGiftItems?.find(
    item => item.type === 'gift'
  )?.giftItem;

  if (productRes.badges) {
    preparedBadges = prepareOldBadges(
      productRes.badges,
      productAsGift?.id ? null : productRes.discounPriceField,
      isDigital,
      isBuyAtOncePromo ? multiPricePromo : null,
      productAsGift ? { id: productAsGift?.id } : null
    );
  }

  if (productRes.badgesList) {
    preparedNewBadges = prepareNewBadges(productRes.badgesList);
    preparedBottomBadges = prepareBottomBadges(
      productRes.is_our,
      productRes.badgesList
    );
  }

  const isPreorder = getPreorderDateLabel(
    nearestAvailableIntervalValue,
    productRes?.delivery?.date_with_timezone,
    { isNearest: productRes?.delivery?.is_nearest }
  );
  const canBeBought = getProductCanBeBought({
    isProductMenu: !!productRes.isMenu,
    isNotForSale: productRes.is_not_for_sale === 'Y',
    pathname
  });
  const productInfo: ProductInfoI = {
    id: productRes.id,
    available: isBarMenu ? !!productRes?.isMenu : !!productRes.available,
    isMenu: !!productRes?.isMenu,
    canBeBought,
    isFavorite: !!productRes?.isFavorite,
    promocodeGiftItems: productRes?.promocodeGiftItems?.length
      ? productRes.promocodeGiftItems
      : [],
    badges: prepareAllBadges([...preparedBadges, ...preparedNewBadges]),
    newBadges: preparedBottomBadges,
    isProductNotForSale: productRes.isMenu
      ? false
      : productRes.is_not_for_sale === 'Y',
    isProductArchieved: productRes.status_id === 5,
    productArticle: productRes.id,
    title: productRes.title || 'Название',
    subtitle: productRes.subtitle || '',
    is_18plus: !!productRes.is_18plus,
    carbs: productRes.carbs,
    proteins: productRes.proteins,
    reference_weight: productRes.reference_weight,
    fats: productRes.fats,
    text:
      typeof productRes.text !== 'undefined' &&
      productRes.text != null &&
      productRes.text !== 'null' &&
      productRes.text.length > 0
        ? productRes.text
        : productRes.seo_md || 'Описание',
    deliveryDate: productRes?.delivery?.date
      ? productRes.delivery.date.toLowerCase()
      : '',
    isVesovoi: productRes.vesovoi === 'Y',
    multiPricePromo,
    measureItem: productRes.measureItem,
    measure_id: productRes.measure_id,
    weight_of_goods: productRes.weight_of_goods,
    country: productRes.country,
    attributes: productRes.attributes,
    productPackage: productRes.package,
    nutritional_value: productRes.nutritional_value,
    energy_value: productRes.energy_value,
    ingredients: productRes.ingredients,
    storage_conditions: productRes.storage_conditions,
    gost: productRes.gost,
    documents: productRes.registryRecordUrls ?? [],
    mostUsefulComment: productRes.mostUsefulComment,
    averageRating: productRes.averageRating,
    supplier_id: productRes.supplier_id,
    isFreeze: !isBarMenu && productRes.war_id === 3,
    ifWeighting: !!(
      productRes.vesovoi === 'Y' &&
      productRes.weight_of_goods &&
      productRes.weight_of_goods > 0 &&
      productRes.weight_of_goods !== 1
    ),
    isDigital,
    videoFromText: productRes?.videoFromText?.length
      ? productRes.videoFromText
      : [],
    isExpressPickupAval:
      !isBarMenu && !!productRes.isExpressPickupAval && !!productRes.available,
    isExpressDeliveryAval: !isBarMenu && productRes.isExpressDeliveryAval,
    sizeRangeItem: productRes.sizeRangeItem,
    how_to_make: productRes.how_to_make ?? '',
    comments_number: productRes.comments_number || 0,
    rating_comments: productRes.rating_comments,

    salesNumberInfo: numberTextFormat(productRes.sales_number || 0),
    status_id: productRes.status_id,
    priceObj: {
      currentPriceField: productRes.currentPriceField,
      currentPriceField2: productRes.currentPriceField2,
      oldPriceField2: productRes.oldPriceField2,
      measureItem: productRes.measureItem,
      weight_of_goods: productRes.weight_of_goods,
      symbolPriceField2: productRes.symbolPriceField2,
      oldPriceField: productRes.oldPriceField,
      symbolPriceField: productRes.symbolPriceField,
      specialPriceData: productRes.specialPriceData,
      priceBlockWeightPrice: productRes.priceBlockWeightPrice,
      discount: productRes.discounPriceField,
      measure_id: productRes.measure_id
    },

    images: productRes?.images,
    variants: productRes.variants || [],
    gds_count: productRes.gds_count,
    productDiscountNotification: productRes.productDiscountNotification,

    price: preparePriceFloatObject(productRes.currentPriceField),

    price2: preparePriceFloatObject(productRes.currentPriceField2),

    supplyDate: productRes.supply_title ?? null,
    bestBeforeDate: productRes.expiration_title ?? null,
    isShowVideoTab: !!productRes?.videos?.length,
    videos: productRes?.videos?.length ? productRes.videos : [],
    isPreorder,
    availableQuantity: (productRes?.availableQuantity || []) as Array<{
      store_id: number;
      amount: number;
    }>
  };
  return {
    productInfo,
    isProductAvailableForRefund: !!res.availableForRefund
  };
};
